import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE } from '../../utils/api'
import { toast } from 'react-toastify'
import { generateLocalitySampleExcel, parseLocalityExcel } from '../../utils/excelTemplate'
import ConfirmModal from '../../components/modals/ConfirmModal';

interface City {
    _id: string;
    name: string;
}

interface Locality {
    _id: string;
    name: string;
    city: City | string | null;
    image?: string;
    images?: string[];
    isActive?: boolean;
    isFeatured?: boolean;
    avgPricePerSqFt?: number;
    growthPercentage?: number;
    computedMarketVal?: number;   // auto-calculated from projects in this locality
    projectCount?: number;        // number of Approved projects in this locality
}

export default function AdminLocalities() {
    const navigate = useNavigate()
    const { token } = useAuth()
    const [localities, setLocalities] = useState<Locality[]>([])
    const [cities, setCities] = useState<City[]>([])
    const [loading, setLoading] = useState(true)
    const [importing, setImporting] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [cityFilter, setCityFilter] = useState('all')
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [currentPage, setCurrentPage] = useState(1)
    const itemsPerPage = 15
    const [confirmModal, setConfirmModal] = useState({ 
        isOpen: false, 
        title: '', 
        message: '', 
        onConfirm: () => {} 
    });

    const fetchData = async () => {
        setLoading(true)
        try {
            const [locRes, cityRes] = await Promise.all([
                fetch(`${API_BASE}/localities`),
                fetch(`${API_BASE}/cities`)
            ])
            const locData = await locRes.json()
            const cityData = await cityRes.json()
            if (Array.isArray(locData)) setLocalities(locData.filter(Boolean))
            if (Array.isArray(cityData)) setCities(cityData.filter(Boolean))
        } catch (err) {
            console.error(err)
            toast.error("Registry sync failed")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { fetchData() }, [])

    const filteredLocalities = useMemo(() => {
        return localities.filter(loc => {
            const matchesSearch = loc.name?.toLowerCase().includes(searchTerm.toLowerCase());
            const cityId = (loc.city && typeof loc.city === 'object') ? loc.city?._id : loc.city;
            const matchesCity = cityFilter === 'all' || cityId === cityFilter;
            return matchesSearch && matchesCity;
        });
    }, [localities, searchTerm, cityFilter]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, cityFilter]);

    const stats = useMemo(() => {
        const total = localities.length;
        const activeCities = cities.length;
        // Use computedMarketVal (from projects) if available, otherwise fall back to manual
        const pricesForAvg = localities.map(loc => loc.computedMarketVal ?? loc.avgPricePerSqFt ?? 0).filter(v => v > 0);
        const avgPrice = pricesForAvg.length > 0
            ? Math.round(pricesForAvg.reduce((acc, v) => acc + v, 0) / pricesForAvg.length)
            : 0;
        return { total, activeCities, avgPrice };
    }, [localities, cities]);

    const totalPages = Math.max(1, Math.ceil(filteredLocalities.length / itemsPerPage));

    const paginatedLocalities = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredLocalities.slice(start, start + itemsPerPage);
    }, [filteredLocalities, currentPage]);

    const handleEdit = (loc: Locality) => {
        navigate(`/admin-edit-locality/${loc._id}`)
    }

    const toggleFeatured = async (loc: Locality) => {
        try {
            const res = await fetch(`${API_BASE}/localities/${loc._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ isFeatured: !loc.isFeatured })
            });

            if (res.ok) {
                toast.success(`Locality ${!loc.isFeatured ? 'featured' : 'unfeatured'}`);
                setLocalities(prev => prev.map(l => l._id === loc._id ? { ...l, isFeatured: !loc.isFeatured } : l));
            } else {
                toast.error("Failed to update featured state");
            }
        } catch (err) {
            console.error(err);
            toast.error("Action failed");
        }
    }

    const handleDelete = (id: string) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Locality',
            message: 'Are you sure you want to delete this locality? This action cannot be undone.',
            onConfirm: async () => {
                try {
                    const res = await fetch(`${API_BASE}/localities/${id}`, {
                        method: 'DELETE',
                        headers: { Authorization: `Bearer ${token}` }
                    })
                    if (res.ok) {
                        toast.success("Locality removed")
                        fetchData()
                        setSelectedIds(prev => prev.filter(sid => sid !== id));
                    }
                } catch {
                    toast.error("Action failed")
                }
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    }

    const deleteSelected = () => {
        if (selectedIds.length === 0) return;
        setConfirmModal({
            isOpen: true,
            title: 'Delete Multiple Localities',
            message: `Are you sure you want to delete ${selectedIds.length} selected localities? This action cannot be undone.`,
            onConfirm: async () => {
                try {
                    const res = await fetch(`${API_BASE}/localities/bulk/delete`, {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ ids: selectedIds })
                    });

                    if (res.ok) {
                        const result = await res.json();
                        toast.success(result.message || 'Localities deleted successfully');
                        fetchData();
                        setSelectedIds([]);
                    } else {
                        const err = await res.json();
                        toast.error(err.message || 'Failed to delete localities');
                    }
                } catch (err) {
                    console.error(err);
                    toast.error('An error occurred while deleting');
                }
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === filteredLocalities.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredLocalities.map(l => l._id));
        }
    };

    const toggleSelectOne = (id: string) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter(sid => sid !== id));
        } else {
            setSelectedIds([...selectedIds, id]);
        }
    };

    const handleSampleDownload = () => {
        generateLocalitySampleExcel();
    }

    const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setImporting(true);
            const data = await parseLocalityExcel(file);
            if (data.length === 0) {
                toast.error("Invalid file");
                setImporting(false);
                return;
            }

            const res = await fetch(`${API_BASE}/localities/bulk-import`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ localities: data })
            });

            const result = await res.json();
            if (res.ok) {
                toast.success(result.message);
                fetchData();
            } else {
                toast.error(result.message || "Import failed");
            }
        } catch (err: any) {
            toast.error(err.message || "Error during import");
        } finally {
            setImporting(false);
            e.target.value = '';
        }
    }

    return (
        <AdminLayout>
            <div className="mx-auto pb-10">
                {/* Modern Banner Header */}
                <div className="bg-[#05144b] rounded-[1.5rem] p-4 md:p-6 mb-6 shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-80 h-80 bg-yellow-400 opacity-5 rounded-full -mr-20 -mt-20 blur-[80px]"></div>
                    
                    <div className="relative z-10 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-8 h-[2px] bg-yellow-400"></div>
                                <span className="text-yellow-400 font-semibold tracking-widest text-xs uppercase">Administrative Hub</span>
                            </div>
                            <h1 className="text-2xl md:text-4xl font-bold text-white mb-2 tracking-tight">
                                Sector <span className="text-yellow-400 italic">Registry</span>
                            </h1>
                            <p className="text-slate-300 text-sm max-w-lg font-normal leading-relaxed">
                                Seamlessly manage metropolitan localities, analyze price trends, and optimize regional data points.
                            </p>
                        </div>

                        <div className="flex flex-row flex-nowrap items-center gap-3 shrink-0">
                            <button
                                className="px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-xl font-medium transition-all flex items-center gap-2 border border-white/10 group text-xs"
                                onClick={handleSampleDownload}
                            >
                                <i className="fa-solid fa-cloud-download text-yellow-400 group-hover:scale-125 transition-transform"></i>
                                Template
                            </button>
                            
                            <div className="relative">
                                <button
                                    className={`px-4 py-2 bg-yellow-400 hover:bg-white hover:text-[#05144b] text-[#05144b] rounded-xl font-bold transition-all shadow-xl flex items-center gap-2 text-xs ${importing ? 'opacity-50' : ''}`}
                                    disabled={importing}
                                >
                                    <i className={`fa-solid ${importing ? 'fa-spinner fa-spin' : 'fa-file-import'}`}></i>
                                    {importing ? 'Syncing...' : 'Bulk Import'}
                                </button>
                                <input
                                    type="file"
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                    accept=".xlsx, .xls"
                                    onChange={handleImport}
                                    disabled={importing}
                                />
                            </div>

                            <button
                                className="px-5 py-2.5 bg-white text-[#05144b] hover:bg-yellow-400 rounded-xl font-bold transition-all shadow-xl flex items-center gap-2 text-xs"
                                onClick={() => navigate('/admin-add-locality')}
                            >
                                <i className="fa-solid fa-plus-circle"></i>
                                Add Locality
                            </button>
                        </div>
                    </div>

                    {/* Integrated Quick Stats */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6 pt-4 border-t border-white/10">
                        <div className="group flex items-center justify-between sm:justify-start sm:gap-4">
                            <div className="text-white text-2xl font-bold tracking-tight group-hover:text-yellow-400 transition-colors">{stats.total}</div>
                            <div className="text-slate-400 text-xs font-semibold uppercase tracking-widest">Active Sectors</div>
                        </div>
                        <div className="group flex items-center justify-between sm:justify-start sm:gap-4">
                            <div className="text-white text-2xl font-bold tracking-tight group-hover:text-yellow-400 transition-colors">{stats.activeCities}</div>
                            <div className="text-slate-400 text-xs font-semibold uppercase tracking-widest">Regional Hubs</div>
                        </div>
                        <div className="group flex items-center justify-between sm:justify-start sm:gap-4">
                            <div className="text-white text-2xl font-bold tracking-tight group-hover:text-yellow-400 transition-colors">₹{stats.avgPrice}</div>
                            <div className="text-slate-400 text-xs font-semibold uppercase tracking-widest">Market Avg / Sqft</div>
                        </div>
                    </div>
                </div>

                {/* Table & Filtering Section */}
                <div className="bg-white rounded-[2rem] shadow-2xl shadow-slate-200/40 border border-slate-100 overflow-hidden">
                    <div className="p-8 border-b border-slate-50 flex flex-col md:flex-row gap-8 justify-between items-center bg-slate-50/20">
                        <div className="relative w-full md:w-96">
                            <i className="fa-solid fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                            <input 
                                type="text" 
                                placeholder="Search localities..."
                                className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-2xl focus:border-[#05144b] outline-none transition-all font-medium text-slate-600 shadow-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="flex items-center gap-4 w-full md:w-auto">
                            <span className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Filter:</span>
                            <select 
                                className="flex-1 md:w-64 px-5 py-4 bg-white border border-slate-200 rounded-2xl focus:border-[#05144b] outline-none font-medium text-slate-600 appearance-none cursor-pointer shadow-sm"
                                value={cityFilter}
                                onChange={(e) => setCityFilter(e.target.value)}
                            >
                                <option value="all">Global (All Cities)</option>
                                {cities.map(c => (
                                    <option key={c._id} value={c._id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        {selectedIds.length > 0 && (
                            <button
                                onClick={deleteSelected}
                                className="flex items-center gap-2 px-6 py-4 bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200 rounded-2xl text-sm font-bold transition-all animate-in fade-in zoom-in-95"
                            >
                                <i className="fa-solid fa-trash-can"></i>
                                <span>Delete Selected ({selectedIds.length})</span>
                            </button>
                        )}
                    </div>

                    <div className="overflow-x-auto">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-40">
                                <div className="w-12 h-12 border-4 border-[#05144b] border-t-yellow-400 rounded-full animate-spin mb-4"></div>
                                <p className="text-slate-400 font-medium">Fetching registry...</p>
                            </div>
                        ) : filteredLocalities.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-40 text-center">
                                <div className="w-24 h-24 bg-slate-50 rounded-[2rem] flex items-center justify-center mb-6 text-slate-200 border border-slate-100">
                                    <i className="fa-solid fa-ghost text-4xl"></i>
                                </div>
                                <h3 className="text-2xl font-bold text-slate-800 tracking-tight">Empty Database</h3>
                                <p className="text-slate-400 font-normal mt-2">No localities match your current criteria.</p>
                            </div>
                        ) : (
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="bg-slate-50/30">
                                        <th className="px-10 py-6">
                                            <input 
                                                type="checkbox"
                                                className="w-5 h-5 rounded-lg border-slate-300 text-[#05144b] focus:ring-[#05144b] cursor-pointer"
                                                checked={filteredLocalities.length > 0 && selectedIds.length === filteredLocalities.length}
                                                onChange={toggleSelectAll}
                                            />
                                        </th>
                                        <th className="px-6 py-6 text-xs font-bold text-[#05144b] uppercase tracking-[0.15em]">Locality Designation</th>
                                        <th className="px-6 py-6 text-xs font-bold text-[#05144b] uppercase tracking-[0.15em]">City Center</th>
                                        <th className="px-6 py-6 text-xs font-bold text-[#05144b] uppercase tracking-[0.15em] text-center">Market Val</th>
                                        <th className="px-6 py-6 text-xs font-bold text-[#05144b] uppercase tracking-[0.15em] text-center">Trend</th>
                                        <th className="px-6 py-6 text-xs font-bold text-[#05144b] uppercase tracking-[0.15em] text-center">Featured</th>
                                        <th className="px-10 py-6 text-xs font-bold text-[#05144b] uppercase tracking-[0.15em] text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {paginatedLocalities.map((loc) => (
                                        <tr key={loc._id} className={`hover:bg-yellow-50/40 transition-colors group ${selectedIds.includes(loc._id) ? 'bg-yellow-50/60' : ''}`}>
                                            <td className="px-10 py-8">
                                                <input 
                                                    type="checkbox"
                                                    className="w-5 h-5 rounded-lg border-slate-300 text-[#05144b] focus:ring-[#05144b] cursor-pointer"
                                                    checked={selectedIds.includes(loc._id)}
                                                    onChange={() => toggleSelectOne(loc._id)}
                                                />
                                            </td>
                                            <td className="px-6 py-8">
                                                <div className="flex items-center gap-6">
                                                    <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center text-[#05144b] font-bold text-xl group-hover:bg-[#05144b] group-hover:text-yellow-400 transition-all">
                                                        {loc.name?.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <div className="text-lg font-bold text-slate-800 mb-1 group-hover:text-[#05144b] transition-colors">{loc.name}</div>
                                                        <div className="inline-flex items-center px-2 py-0.5 bg-slate-100 rounded text-[10px] font-bold text-slate-400 uppercase tracking-tighter">LOC_{loc._id.substring(0, 8)}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-8">
                                                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#05144b]/5 rounded-xl text-sm font-semibold text-[#05144b]">
                                                    <i className="fa-solid fa-location-dot text-[10px] text-yellow-500"></i>
                                                    {loc.city && typeof loc.city === 'object' ? loc.city?.name : typeof loc.city === 'string' ? loc.city : 'Orphan'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-8 text-center">
                                                <div className="text-lg font-bold text-slate-800">
                                                    ₹{(loc.computedMarketVal ?? loc.avgPricePerSqFt ?? 0).toLocaleString('en-IN')}
                                                </div>
                                                {(loc.projectCount ?? 0) > 0 ? (
                                                    <div className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-blue-50 border border-blue-100 rounded-full">
                                                        <i className="fa-solid fa-building text-[8px] text-blue-400"></i>
                                                        <span className="text-[10px] font-bold text-blue-500">{loc.projectCount} Project{(loc.projectCount ?? 0) > 1 ? 's' : ''} Avg</span>
                                                    </div>
                                                ) : (
                                                    <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-1">Manual Value</div>
                                                )}
                                            </td>
                                            <td className="px-6 py-8 text-center">
                                                <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold ${(loc.growthPercentage || 0) >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                                                    <i className={`fa-solid fa-arrow-trend-${(loc.growthPercentage || 0) >= 0 ? 'up' : 'down'}`}></i>
                                                    {loc.growthPercentage || 0}%
                                                </div>
                                            </td>
                                            <td className="px-6 py-8 text-center">
                                                <button 
                                                    onClick={() => toggleFeatured(loc)}
                                                    className={`px-3 py-1 rounded-xl text-xs font-bold transition-all border ${loc.isFeatured ? 'bg-amber-50 border-amber-200 text-amber-600' : 'bg-slate-50 border-slate-200 text-slate-400'}`}
                                                >
                                                    <i className={`fa-solid fa-star me-1 ${loc.isFeatured ? 'text-amber-500' : 'text-slate-300'}`}></i>
                                                    {loc.isFeatured ? 'Featured' : 'Enable'}
                                                </button>
                                            </td>
                                            <td className="px-10 py-8 text-right">
                                                <div className="flex justify-end gap-3">
                                                    <button 
                                                        className="w-12 h-12 rounded-2xl bg-white border border-slate-200 text-[#05144b] hover:bg-yellow-400 hover:border-yellow-400 transition-all flex items-center justify-center shadow-sm"
                                                        onClick={() => handleEdit(loc)}
                                                    >
                                                        <i className="fa-solid fa-pen-fancy text-sm"></i>
                                                    </button>
                                                    <button 
                                                        className="w-12 h-12 rounded-2xl bg-white border border-slate-200 text-rose-500 hover:bg-[#05144b] hover:text-white hover:border-[#05144b] transition-all flex items-center justify-center shadow-sm"
                                                        onClick={() => handleDelete(loc._id)}
                                                    >
                                                        <i className="fa-solid fa-trash-alt text-sm"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="px-10 py-6 border-t border-slate-50 bg-slate-50/20 flex justify-between items-center text-sm font-medium text-slate-400">
                        <div>
                            Showing <span className="font-bold text-[#05144b]">{paginatedLocalities.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}</span> to <span className="font-bold text-[#05144b]">{Math.min(currentPage * itemsPerPage, filteredLocalities.length)}</span> of <span className="font-bold text-[#05144b]">{filteredLocalities.length}</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-xs text-slate-400 font-bold uppercase tracking-widest">Page {currentPage} of {totalPages}</span>
                            <button 
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-6 py-2 bg-white text-[#05144b] border border-slate-200 rounded-xl uppercase text-[10px] font-bold tracking-widest hover:bg-slate-50 transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Back
                            </button>
                            <button 
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-6 py-2 bg-[#05144b] text-white rounded-xl uppercase text-[10px] font-bold tracking-widest hover:bg-yellow-400 hover:text-[#05144b] transition-all shadow-lg disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                confirmText="Delete Now"
                cancelText="Keep Locality"
                type="danger"
            />
        </AdminLayout>
    )
}
